import { Stack, useNavigation } from 'expo-router';
import Head from 'expo-router/head';
import { ChevronLeft } from 'lucide-react-native';
import { isValidElement, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import type { HeaderProps } from './header.types';
import { Button, ButtonIcon, ButtonText } from './ui/button';
import { Heading } from './ui/heading';
import { HStack } from './ui/hstack';
import { Text } from './ui/text';
import { View } from './ui/view';

import { HeaderItemRow } from '@/components/headerItems/HeaderItemRow';
import { hasVisibleHeaderItems } from '@/components/headerItems/hasVisibleHeaderItems';
import { InHeaderSlot } from '@/components/headerSlot';
import { NAV_BAR_GUTTER_CLASS } from '@/constants/navBar';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { documentTitle } from '@/util/documentTitle';

export type { HeaderProps } from './header.types';

/**
 * Header Component (Web)
 *
 * Web-specific header with three-column responsive layout (left | title | right). It spans the
 * window with the nav bar's side padding, so Back sits under the logo and the right items under
 * the account menu on every page. The focused page's header is drawn in the layout's header slot
 * (`HeaderSlot`), so where a screen renders it does not move it.
 * Disables React Navigation's native header and renders web-appropriate chrome.
 * Back goes where the browser's back button would, or to `backRoute` when that would leave the
 * app; see `useGoBack`. Custom left/right actions are supported too. A slot given
 * `leftItems` or `rightItems` draws those and ignores its render function, as on native.
 * `fill="brand"` paints the row brand-colored and turns its items light. Every other header
 * is `bg-background-0`, the same on every page.
 *
 * A string `title` on a shown header also names the browser tab ("Title · Primum"). A hidden
 * header's title is only the native large title, so it names nothing here.
 */
export function Header({
  title,
  subtitle,
  isBackButton = false,
  backButtonText = 'Back',
  isLarge = false,
  backRoute,
  backRouteParams,
  headerShown = true,
  headerLeft,
  headerRight,
  headerTitle,
  headerShadowVisible,
  className,
  leftItems,
  rightItems,
  fill = 'default',
  documentTitle: tabTitle,
}: HeaderProps) {
  const navigation = useNavigation();

  const { navigateBack } = useBackNavigation(backRoute, backRouteParams);

  useEffect(() => {
    navigation.setOptions({
      headerShown: false,
    });
  }, [navigation]);

  const tabTitleText = tabTitle || (typeof title === 'string' ? title : undefined);

  const pageTitle = tabTitleText ? (
    <Head>
      <title>{documentTitle(tabTitleText)}</title>
    </Head>
  ) : null;

  if (!headerShown) {
    return <Stack.Screen options={{ headerShown: false }} />;
  }

  const hasContent =
    Boolean(title) ||
    Boolean(headerTitle) ||
    isBackButton ||
    Boolean(headerLeft) ||
    Boolean(headerRight) ||
    hasVisibleHeaderItems(leftItems) ||
    hasVisibleHeaderItems(rightItems);

  if (!hasContent) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <InHeaderSlot hasContent={false} />
      </>
    );
  }

  const itemTone = fill === 'brand' ? 'onBrand' : 'default';

  const backButton = isBackButton ? (
    <Button
      variant="link"
      size="md"
      action="primary"
      onPress={navigateBack}
      className="flex-row items-center gap-2">
      <ButtonIcon as={ChevronLeft} className="h-6 w-6" />
      <ButtonText>{backButtonText}</ButtonText>
    </Button>
  ) : null;

  const renderLeft = () => {
    if (backButton) {
      return <HStack className="items-center">{backButton}</HStack>;
    }

    if (leftItems && leftItems.length > 0) {
      return <HeaderItemRow items={leftItems} tone={itemTone} />;
    }

    if (typeof headerLeft === 'function') {
      return (
        <HStack space="md" className="items-center">
          {headerLeft({ tintColor: '', canGoBack: false })}
        </HStack>
      );
    }

    if (isValidElement(headerLeft)) {
      return (
        <HStack space="md" className="items-center">
          {headerLeft}
        </HStack>
      );
    }

    return null;
  };

  const renderTitle = () => {
    if (typeof headerTitle === 'function') {
      return headerTitle({ tintColor: '', children: typeof title === 'string' ? title : '' });
    }

    if (isValidElement(headerTitle)) {
      return headerTitle;
    }

    if (isValidElement(title)) {
      return title;
    }

    if (title) {
      const headingSize = isLarge ? 'h1-2xl' : 'h1-lg';

      return (
        <Heading className="truncate text-center" size={headingSize}>
          {title}
        </Heading>
      );
    }

    return null;
  };

  const renderRight = () => {
    if (rightItems && rightItems.length > 0) {
      return <HeaderItemRow items={rightItems} tone={itemTone} />;
    }

    if (typeof headerRight === 'function') {
      return headerRight({ tintColor: '', canGoBack: false });
    }

    if (isValidElement(headerRight)) {
      return headerRight;
    }

    return null;
  };

  const titleContent = renderTitle();

  const background = fill === 'brand' ? 'bg-primary-500' : 'bg-background-0';

  return (
    <>
      {pageTitle}
      <Stack.Screen options={{ headerShown: false }} />
      <InHeaderSlot>
        <HStack
          className={twMerge(
            'min-h-16 w-full items-center justify-center',
            background,
            headerShadowVisible && 'border-b border-outline-200',
            className
          )}>
          <HStack className={twJoin('w-full items-center', NAV_BAR_GUTTER_CLASS)}>
            <HStack className="min-w-0 flex-1 items-center justify-start">{renderLeft()}</HStack>

            {titleContent && (
              <HStack className="min-w-0 flex-1 items-center justify-center overflow-hidden px-2">
                {titleContent}
              </HStack>
            )}

            <HStack className="min-w-0 flex-1 items-center justify-end">{renderRight()}</HStack>
          </HStack>
        </HStack>
        {subtitle &&
          (typeof subtitle === 'string' ? (
            <Text className={twJoin('pb-2', background, NAV_BAR_GUTTER_CLASS)}>{subtitle}</Text>
          ) : (
            <View className={twJoin('w-full', background, NAV_BAR_GUTTER_CLASS)}>{subtitle}</View>
          ))}
      </InHeaderSlot>
    </>
  );
}
